/**
 * Cart Threshold Popup
 * Opens a dialog once the cart total reaches the threshold of the rule matching
 * the visitor's country (or the default rule). Re-checks the cart after:
 * - theme events (`ajaxProduct:added`, `cart:updated`) – the theme mutates the
 *   cart via the Ajax Cart API, which doesn't emit Shopify's storefront events
 * - Shopify storefront events (`shopify:cart:*`) – emitted by
 *   `Shopify.actions.updateCart`, e.g. from apps
 * - Upnova side cart (`upez__chainEventManager.onCartChanged`) – Upnova wraps
 *   fetch and reports the full cart after every /cart/add, /cart/change and
 *   discount change, including quantity edits inside its drawer
 */
const UPNOVA_WAIT_TIMEOUT = 10000;
const STOREFRONT_CART_EVENTS = [
  'shopify:cart:lines-update',
  'shopify:cart:discount-update',
];
const COUNTRY_STORAGE_KEY = 'cart-threshold-popup:country';

class CartThresholdPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.config = this.parseConfig();
    this.delay = Number(this.dataset.delay) || 0;
    this.storageKey = this.dataset.storageKey;
    this.showOnce = this.hasAttribute('data-show-once');
    this.designMode = this.hasAttribute('data-design-mode');
    this.rule = null;
    this.timer = null;

    this.onCartChange = this.onCartChange.bind(this);
    this.onStorefrontCartEvent = this.onStorefrontCartEvent.bind(this);
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.onSectionDeselect = this.onSectionDeselect.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    // Close on backdrop click
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    if (this.designMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      return;
    }

    if (this.showOnce && this.hasBeenShown()) return;

    document.addEventListener('ajaxProduct:added', this.onCartChange);
    document.addEventListener('cart:updated', this.onCartChange);
    STOREFRONT_CART_EVENTS.forEach((name) => document.addEventListener(name, this.onStorefrontCartEvent));
    this.subscribeToUpnova();

    this.ready = this.resolveRule();
    this.ready.then(() => this.check(Number(this.dataset.cartTotal) || 0));
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('ajaxProduct:added', this.onCartChange);
    document.removeEventListener('cart:updated', this.onCartChange);
    STOREFRONT_CART_EVENTS.forEach((name) => document.removeEventListener(name, this.onStorefrontCartEvent));
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  parseConfig() {
    const script = this.querySelector('[data-popup-rules]');

    try {
      const config = JSON.parse(script?.textContent || '{}');
      return {
        rules: Array.isArray(config.rules) ? config.rules : [],
        fallbackRule: config.fallbackRule || null,
      };
    } catch (error) {
      console.error('CartThresholdPopup: invalid rules JSON', error);
      return { rules: [], fallbackRule: null };
    }
  }

  /**
   * Picks the first rule whose countries include the visitor's country,
   * falling back to the default rule. Only looks up the country when
   * country rules exist.
   */
  async resolveRule() {
    if (this.config.rules.length) {
      const country = await this.getCustomerCountry();
      this.rule = this.config.rules.find((rule) => country && rule.countries.includes(country)) || null;
    }

    if (!this.rule) this.rule = this.config.fallbackRule;
    if (this.rule) this.showPanel(this.rule.id);
  }

  /**
   * Geo-detected country (same source as the redirect banner), cached for the
   * session. Falls back to the current market's country.
   */
  async getCustomerCountry() {
    try {
      const cached = sessionStorage.getItem(COUNTRY_STORAGE_KEY);
      if (cached) return cached;
    } catch (error) {
      // Storage unavailable – fetch below
    }

    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json?country[enabled]=true`,
      );
      const data = await response.json();
      const country = data?.detected_values?.country?.handle?.toUpperCase();

      if (country) {
        try {
          sessionStorage.setItem(COUNTRY_STORAGE_KEY, country);
        } catch (error) {
          // Storage unavailable – country is fetched again next page
        }
        return country;
      }
    } catch (error) {
      console.error('CartThresholdPopup: unable to detect country', error);
    }

    return (this.dataset.marketCountry || '').toUpperCase();
  }

  /**
   * Threshold is set in the shop currency; convert it to the customer's
   * presentment currency so it compares against cart.total_price correctly.
   */
  thresholdInPresentmentCurrency(threshold) {
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    return threshold * rate;
  }

  async onCartChange(event) {
    await this.ready;
    if (!this.rule) return;

    const eventCart = event?.detail?.cart;

    if (eventCart && typeof eventCart.total_price === 'number') {
      this.check(eventCart.total_price);
      return;
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      this.check(cart.total_price);
    } catch (error) {
      console.error('CartThresholdPopup: unable to fetch cart', error);
    }
  }

  /**
   * Storefront events fire before the cart changes and carry a promise that
   * resolves with the updated cart. Its total is a decimal MoneyV2 amount, so
   * re-read /cart.js to compare in subunits like the other sources.
   */
  onStorefrontCartEvent(event) {
    if (!event.promise) return;

    event.promise
      .then(({ cart, userErrors } = {}) => {
        if (cart && !userErrors?.length) this.onCartChange();
      })
      .catch(() => {
        // Cart operation failed – total unchanged
      });
  }

  /**
   * Upnova's cart manager is created by its app embed and has no unsubscribe,
   * so the callback is ignored once this element is removed.
   */
  async subscribeToUpnova() {
    const manager = await this.waitForUpnova();
    if (!manager || !this.isConnected) return;

    manager.onCartChanged((cart) => {
      if (this.isConnected) this.onCartChange({ detail: { cart } });
    });
  }

  waitForUpnova() {
    const getManager = () =>
      typeof window.upez__chainEventManager?.onCartChanged === 'function'
        ? window.upez__chainEventManager
        : null;

    return new Promise((resolve) => {
      const start = Date.now();
      const poll = () => {
        const manager = getManager();
        if (manager || Date.now() - start > UPNOVA_WAIT_TIMEOUT) return resolve(manager);
        setTimeout(poll, 250);
      };
      poll();
    });
  }

  check(totalPrice) {
    if (!this.rule || this.dialog.open || this.timer) return;

    const threshold = Number(this.rule.threshold) || 0;
    if (threshold <= 0) return;
    if (totalPrice < this.thresholdInPresentmentCurrency(threshold)) return;
    if (this.showOnce && this.hasBeenShown()) return;

    this.timer = setTimeout(() => {
      this.timer = null;
      this.open();
    }, this.delay);
  }

  showPanel(panelId) {
    const panels = this.querySelectorAll('[data-popup-panel]');
    let activePanel = null;

    panels.forEach((panel) => {
      const isActive = panel.dataset.popupPanel === panelId;
      panel.hidden = !isActive;
      if (isActive) activePanel = panel;
    });

    if (activePanel) {
      this.dialog.setAttribute('aria-labelledby', activePanel.dataset.titleId);
    }
  }

  open() {
    if (this.dialog.open) return;

    this.dialog.showModal();

    if (this.showOnce && !this.designMode) {
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch (error) {
        // Storage unavailable (e.g. private mode) – popup may show again
      }
    }
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  hasBeenShown() {
    try {
      return sessionStorage.getItem(this.storageKey) === '1';
    } catch (error) {
      return false;
    }
  }

  onSectionSelect(event) {
    if (event.detail.sectionId !== this.dataset.sectionId) return;
    this.showPanel('default');
    this.open();
  }

  onSectionDeselect(event) {
    if (event.detail.sectionId === this.dataset.sectionId) this.close();
  }

  onBlockSelect(event) {
    if (event.detail.sectionId !== this.dataset.sectionId) return;
    this.showPanel(event.detail.blockId);
    this.open();
  }
}

if (!customElements.get('cart-threshold-popup')) {
  customElements.define('cart-threshold-popup', CartThresholdPopup);
}
